import { useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck,
  CheckCircle2,
  Circle,
  Clock3,
  AlertTriangle,
  ExternalLink,
  Printer,
  RotateCcw,
  Loader2,
  TrendingUp,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * ReadinessTab
 *
 * Admin-only Charter Readiness Scorecard. Pulls the full NCUA submission
 * checklist from GET /api/admin/credit-union/readiness, lets Greg mark
 * manual items complete / in-progress, attach a document link, and leave
 * notes.
 *
 * The point: one page, every requirement, zero ambiguity about what's
 * left before filing the NCUA charter application.
 */

const STATUS_META = {
  complete: {
    label: "Complete",
    icon: CheckCircle2,
    bg: "bg-green-500/15",
    border: "border-green-400/40",
    text: "text-green-300",
    dot: "#22c55e",
  },
  in_progress: {
    label: "In Progress",
    icon: Clock3,
    bg: "bg-amber-500/15",
    border: "border-amber-400/40",
    text: "text-amber-300",
    dot: "#f59e0b",
  },
  not_started: {
    label: "Not Started",
    icon: Circle,
    bg: "bg-bdu-cream/5",
    border: "border-bdu-cream/15",
    text: "text-bdu-cream/55",
    dot: "#6b7280",
  },
  not_applicable: {
    label: "N / A",
    icon: AlertTriangle,
    bg: "bg-bdu-cream/5",
    border: "border-bdu-cream/15",
    text: "text-bdu-cream/40",
    dot: "#525252",
  },
};

const OVERALL_META = {
  ready: { label: "Charter-ready", color: "#22c55e", hint: "Submit the packet to NCUA Region III." },
  final_push: { label: "Final push", color: "#D4AF37", hint: "Within reach — knock out the last manual items." },
  mid_charter: { label: "Mid-charter", color: "#f59e0b", hint: "Half the packet done. Keep building supporters + capital." },
  early: { label: "Early stage", color: "#ef4444", hint: "Keep stacking pledges, organizers, and policies." },
  not_started: { label: "Not started", color: "#525252", hint: "Begin with organizers and the first 500 pledges." },
};

function formatUnit(value, unit) {
  if (value === undefined || value === null) return "—";
  switch (unit) {
    case "usd":
      return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    case "pct":
      return `${Math.round(Number(value) * 100)}%`;
    case "file":
      return value ? "Published" : "Missing";
    default:
      return Number(value).toLocaleString();
  }
}

function formatTarget(target, unit) {
  if (!target) return "";
  if (unit === "usd") return `$${Number(target).toLocaleString()}`;
  if (unit === "pct") return `${Math.round(Number(target) * 100)}%`;
  if (unit === "file") return "Required";
  return `${Number(target).toLocaleString()}`;
}

function OverallGauge({ pct, overallStatus }) {
  const meta = OVERALL_META[overallStatus] || OVERALL_META.not_started;
  const size = 220;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div className="flex flex-col items-center text-center" data-testid="readiness-overall-gauge">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <defs>
            <linearGradient id="readiness-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={meta.color} stopOpacity="0.9" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.9" />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="url(#readiness-gradient)"
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.4,0,0.2,1)" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50">
            Charter readiness
          </div>
          <div
            className="mt-1 font-serif text-5xl text-bdu-cream"
            data-testid="readiness-overall-pct"
          >
            {pct.toFixed(0)}
            <span className="text-2xl text-bdu-cream/60">%</span>
          </div>
          <div
            className="mt-1 text-xs uppercase tracking-widest font-semibold"
            style={{ color: meta.color }}
            data-testid="readiness-overall-label"
          >
            {meta.label}
          </div>
        </div>
      </div>
      <p className="mt-4 text-sm text-bdu-cream/60 max-w-xs leading-relaxed">
        {meta.hint}
      </p>
    </div>
  );
}

function SectionCard({ section, onEdit }) {
  const complete = section.items.filter((i) => i.status === "complete").length;
  const inProgress = section.items.filter((i) => i.status === "in_progress").length;
  const notApplicable = section.items.filter((i) => i.status === "not_applicable").length;
  const total = section.items.length;
  const countable = Math.max(1, total - notApplicable);
  const pct = Math.round((complete / countable) * 100);

  return (
    <div
      className="bg-bdu-surface2 border border-bdu-gold/15 overflow-hidden"
      data-testid={`readiness-section-${section.key}`}
    >
      {/* Section header */}
      <div className="px-6 py-5 border-b border-bdu-gold/10 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="label-eyebrow">{section.citation}</div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">
            {section.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-bdu-cream/55">
            <span className="text-green-300">
              {complete} complete
            </span>
            {inProgress > 0 && (
              <>
                <span className="text-bdu-cream/25">·</span>
                <span className="text-amber-300">{inProgress} in progress</span>
              </>
            )}
            <span className="text-bdu-cream/25">·</span>
            <span>{total} items</span>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <div className="font-serif text-3xl text-bdu-gold">{pct}%</div>
          <div className="mt-1 text-[0.6rem] uppercase tracking-widest text-bdu-cream/40">
            section
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="divide-y divide-bdu-gold/5">
        {section.items.map((item) => (
          <ReadinessItem key={item.id} item={item} onEdit={onEdit} />
        ))}
      </div>
    </div>
  );
}

function ReadinessItem({ item, onEdit }) {
  const meta = STATUS_META[item.status] || STATUS_META.not_started;
  const StatusIcon = meta.icon;
  const isAuto = item.source === "auto";
  const hasManual = Boolean(item.manual_status);

  return (
    <div
      className="px-6 py-5 flex items-start gap-5 hover:bg-black/20 transition-colors"
      data-testid={`readiness-item-${item.id}`}
    >
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full border ${meta.border} ${meta.bg} flex items-center justify-center`}
      >
        <StatusIcon size={18} className={meta.text} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <div className="font-sans font-semibold text-bdu-cream text-sm sm:text-base leading-snug">
            {item.title}
          </div>
          <div
            className={`text-[0.6rem] uppercase tracking-widest font-semibold ${meta.text} px-2 py-0.5 rounded-full ${meta.bg} ${meta.border} border`}
            data-testid={`readiness-item-${item.id}-status`}
          >
            {meta.label}
          </div>
          {isAuto && (
            <div className="text-[0.55rem] uppercase tracking-widest text-bdu-cream/40 px-2 py-0.5 rounded-full border border-bdu-cream/15">
              Auto
            </div>
          )}
          {hasManual && (
            <div className="text-[0.55rem] uppercase tracking-widest text-bdu-gold px-2 py-0.5 rounded-full border border-bdu-gold/40">
              Override
            </div>
          )}
        </div>
        <p className="mt-1.5 text-xs text-bdu-cream/55 leading-relaxed">
          {item.description}
        </p>

        {/* Auto progress line */}
        {isAuto && item.target != null && (
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-bdu-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${Math.min(100, (item.value / Math.max(1, item.target)) * 100)}%`,
                  background: `linear-gradient(90deg, ${meta.dot}, #D4AF37)`,
                  transition: "width 800ms cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </div>
            <div className="text-[0.65rem] text-bdu-cream/55 whitespace-nowrap font-mono tabular-nums">
              {formatUnit(item.value, item.unit)}{" "}
              <span className="text-bdu-cream/30">
                / {formatTarget(item.target, item.unit)}
              </span>
            </div>
          </div>
        )}

        {/* Manual notes + doc */}
        {(item.manual_notes || item.document_url) && (
          <div className="mt-3 bg-black/40 border border-bdu-gold/10 px-3 py-2 rounded-sm">
            {item.manual_notes && (
              <div className="text-xs text-bdu-cream/70 whitespace-pre-line">
                {item.manual_notes}
              </div>
            )}
            {item.document_url && (
              <a
                href={item.document_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-bdu-gold hover:underline"
                data-testid={`readiness-item-${item.id}-doc-link`}
              >
                <FileText size={12} /> Attached document <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => onEdit(item)}
        className="flex-shrink-0 text-xs text-bdu-gold hover:underline uppercase tracking-widest"
        data-testid={`readiness-item-${item.id}-edit-btn`}
      >
        {hasManual ? "Edit" : "Mark"}
      </button>
    </div>
  );
}

function EditOverrideModal({ item, onClose, onSaved }) {
  const [status, setStatus] = useState(
    item.manual_status || item.auto_status || "not_started"
  );
  const [notes, setNotes] = useState(item.manual_notes || "");
  const [documentUrl, setDocumentUrl] = useState(item.document_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(
        `/admin/credit-union/readiness/${encodeURIComponent(item.id)}`,
        { manual_status: status, manual_notes: notes, document_url: documentUrl }
      );
      toast.success("Status updated");
      onSaved();
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) {
        toast.error("Session expired — please log in again.");
      } else {
        toast.error(
          formatApiError(e?.response?.data?.detail) || "Failed to save"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(
        `/admin/credit-union/readiness/${encodeURIComponent(item.id)}`
      );
      toast.success("Override cleared");
      onSaved();
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) {
        toast.error("Session expired — please log in again.");
      } else {
        toast.error(
          formatApiError(e?.response?.data?.detail) || "Failed to reset"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="readiness-edit-modal"
    >
      <div
        className="bg-bdu-surface2 border border-bdu-gold/30 max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-bdu-gold/15">
          <div className="label-eyebrow">Update readiness item</div>
          <h3 className="mt-2 font-serif text-xl text-bdu-cream leading-tight">
            {item.title}
          </h3>
          <p className="mt-2 text-xs text-bdu-cream/55 leading-relaxed">
            {item.description}
          </p>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <div className="label-eyebrow mb-2">Status</div>
            <div className="grid grid-cols-2 gap-2">
              {["complete", "in_progress", "not_started", "not_applicable"].map((s) => {
                const m = STATUS_META[s];
                const active = status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    data-testid={`readiness-modal-status-${s}`}
                    className={`px-3 py-2.5 text-xs uppercase tracking-widest border transition flex items-center gap-2 ${
                      active
                        ? `${m.bg} ${m.border} ${m.text} font-semibold`
                        : "bg-black/30 border-bdu-cream/10 text-bdu-cream/60 hover:border-bdu-cream/30"
                    }`}
                  >
                    <m.icon size={14} /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2" htmlFor="readiness-modal-notes">
              Notes (optional, max 2000)
            </label>
            <textarea
              id="readiness-modal-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
              rows={4}
              placeholder="Add context, blocker, or next step…"
              className="w-full bg-black/40 border border-bdu-cream/15 px-3 py-2 text-sm text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition rounded-sm"
              data-testid="readiness-modal-notes"
            />
          </div>

          <div>
            <label className="label-eyebrow block mb-2" htmlFor="readiness-modal-doc">
              Document URL (optional)
            </label>
            <input
              id="readiness-modal-doc"
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.target.value.slice(0, 1000))}
              placeholder="https://…"
              className="w-full bg-black/40 border border-bdu-cream/15 px-3 py-2 text-sm text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition rounded-sm"
              data-testid="readiness-modal-doc"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-bdu-gold/15 flex items-center justify-between flex-wrap gap-3">
          <button
            type="button"
            onClick={reset}
            disabled={saving || !item.manual_status}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-red-400 hover:text-red-300 disabled:text-bdu-cream/25 disabled:cursor-not-allowed"
            data-testid="readiness-modal-reset-btn"
          >
            <RotateCcw size={12} /> Reset to auto
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-widest text-bdu-cream/60 hover:text-bdu-cream px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn-gold text-xs"
              data-testid="readiness-modal-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReadinessTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/credit-union/readiness")
      .then((r) => {
        setData(r.data);
        setErr(null);
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          setErr(
            "Your admin session has expired. Please log out and sign back in to view the scorecard."
          );
        } else {
          setErr(
            formatApiError(e?.response?.data?.detail) ||
              "Failed to load scorecard"
          );
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const bigNumbers = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Total items", value: data.total_items },
      { label: "Complete", value: data.complete_items, color: "text-green-300" },
      { label: "In progress", value: data.in_progress_items, color: "text-amber-300" },
      {
        label: "Remaining",
        value:
          (data.total_items || 0) -
          (data.complete_items || 0) -
          (data.in_progress_items || 0) -
          (data.not_applicable_items || 0),
        color: "text-red-300",
      },
    ];
  }, [data]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-20 text-bdu-cream/50"
        data-testid="readiness-loading"
      >
        <Loader2 className="animate-spin mr-3" /> Computing readiness…
      </div>
    );
  }
  if (err) {
    const isAuthErr = err.toLowerCase().includes("session has expired");
    return (
      <div
        className="bg-red-500/10 border border-red-400/30 text-red-200 p-6 rounded-sm"
        data-testid="readiness-error"
      >
        <div className="font-semibold text-red-100">{err}</div>
        {isAuthErr && (
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="/login"
              className="btn-gold text-xs"
              data-testid="readiness-relogin-btn"
            >
              Log in again
            </a>
            <button
              type="button"
              onClick={load}
              className="btn-ghost text-xs"
              data-testid="readiness-retry-btn"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-10" data-testid="admin-readiness-tab">
      {/* Header card */}
      <div className="bg-gradient-to-br from-bdu-surface2 to-black border border-bdu-gold/20 p-8">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-4 flex justify-center">
            <OverallGauge pct={data.overall_pct} overallStatus={data.overall_status} />
          </div>
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 label-eyebrow">
              <ClipboardCheck size={14} className="text-bdu-gold" />
              Charter Readiness Scorecard
            </div>
            <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-bdu-cream leading-tight">
              Everything we need before we file the NCUA packet.
            </h2>
            <p className="mt-3 text-sm text-bdu-cream/60 leading-relaxed max-w-2xl">
              Live-updated as pledges arrive, organizers are added, and documents
              are uploaded. Manual items (policies, bylaws, legal) are tracked
              with status, notes, and document links so nothing falls between
              the cracks.
            </p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {bigNumbers.map((n) => (
                <div
                  key={n.label}
                  className="bg-black/40 border border-bdu-cream/10 px-4 py-3"
                  data-testid={`readiness-stat-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50">
                    {n.label}
                  </div>
                  <div className={`mt-1 font-serif text-2xl ${n.color || "text-bdu-cream"}`}>
                    {n.value}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={load}
                className="btn-ghost text-xs"
                data-testid="readiness-refresh-btn"
              >
                <TrendingUp size={14} /> Refresh
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="btn-ghost text-xs"
                data-testid="readiness-print-btn"
              >
                <Printer size={14} /> Print / Save as PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section summary chips */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="readiness-section-summary">
        {data.section_summary.map((s) => (
          <div
            key={s.key}
            className="bg-bdu-surface2 border border-bdu-gold/10 px-4 py-3"
          >
            <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50">
              {s.title}
            </div>
            <div className="mt-1 flex items-end justify-between">
              <div className="font-serif text-2xl text-bdu-cream">
                {s.complete}
                <span className="text-sm text-bdu-cream/40"> / {s.total}</span>
              </div>
              <div className="font-serif text-sm text-bdu-gold">{s.pct}%</div>
            </div>
            <div className="mt-2 h-1 bg-bdu-cream/10 overflow-hidden">
              <div
                className="h-full bg-bdu-gold"
                style={{ width: `${s.pct}%`, transition: "width 700ms" }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Section cards */}
      <div className="space-y-6">
        {data.sections.map((section) => (
          <SectionCard key={section.key} section={section} onEdit={setEditing} />
        ))}
      </div>

      {/* Compliance footer */}
      <div className="text-[0.65rem] uppercase tracking-widest text-bdu-cream/35 text-center leading-relaxed">
        Proposed Trillion Dollar Spending Power Federal Credit Union · Pending NCUA Charter Approval
        <br />
        This scorecard is an internal planning tool, not an NCUA filing. All figures reflect live app state as of{" "}
        {data.generated_at ? new Date(data.generated_at).toLocaleString() : "now"}.
      </div>

      {editing && (
        <EditOverrideModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
